<template>
  <v-container>   
    <GoBackAtTop
      :to="{name: 'CompanyWorkplaces', params: {id: $route.params.id, tab: tab}}"
      :label="$t('labels.workplaces')"
    />
    <v-row no-gutters dense class="title-icon">
      <v-col cols="10">
        <h1 v-if="$route.params.workplace_id == 'new'">{{ $t('company.add-new') }}</h1>
        <h1 v-else>{{ workplace.name }}</h1>
      </v-col>
    </v-row>
    <MessageBox class="mb-5" type="info">
      <h6>{{ $t('company.wp-add-message-title') }}</h6>
      <p>{{ $t('company.wp-add-message-text') }}</p>
    </MessageBox>
    <v-form ref="form" v-model="valid" :lazy-validation="true" v-if="isAdminRole">
      <v-text-field outlined :label="$t('labels.workplace-name')" v-model="workplace.name" :rules="[rules.required]" />

      <v-text-field outlined :label="$t('fields.Adressat')" v-model="workplace.addressee" />
      <v-text-field
        outlined
        :label="$t('fields.address')"
        v-model="workplace.postalAddress1"
        :rules="[rules.required]"
      />
      <v-text-field outlined :label="$t('fields.address2')" v-model="workplace.postalAddress2" />

      <v-text-field
        outlined
        :label="$t('fields.zip')"
        v-model="workplace.postalZip"
        :rules="[rules.required, rules.integer, rules.hasLength(5, workplace.postalZip)]"
      />
      <v-text-field outlined :label="$t('fields.city')" v-model="workplace.postalCity" :rules="[rules.required]" />
      <CountrySelector :showToggle="false" :disabled="false" outlined :label="$t('fields.country')" v-model="workplace.postalCountry" />
      <v-text-field
        outlined
        :label="$t('application.email.e-postadress')"
        v-model="workplace.email"
        :rules="[rules.email]"
      />
      <v-text-field outlined :label="$t('fields.web')" v-model="workplace.web" />
      <v-text-field outlined :label="$t('fields.phone')" v-model="workplace.phone" :rules="[rules.phone]" />
      <template v-if="hasDepartments($route.params.id)">
        <h4 class="mt-5">{{ $t('company.sections') }}</h4>
        <p>{{ $t('company.add-wp-dep-text') }}</p>
        <v-select
          v-model="workplace.departmentIds"
          :items="departments"
          item-value="value"
          item-text="text"
          :label="$t('company.sections')"
          chips
          outlined
          append-icon="$sl-arrow-down"
          multiple
          class=""
        >
          <template v-slot:selection="{attrs, item, selected}">
            <v-chip
              close-icon="$sl-close"
              v-bind="attrs"
              :input-value="selected"
              close
              @click:close="removeDepartment(item)"
              >{{ item.text }}</v-chip
            >
          </template>
        </v-select>
      </template>

      <h4 class="mt-5">{{ $t('fields.invoices') }}</h4>
      <v-row no-gutters dense class="title-icon mt-n2">
        <v-col cols="10">
          <p>{{ $t('company.workplace-text1') }}</p>
        </v-col>
        <v-col class="text-right align-self-center" cols="2">
          <v-switch v-model="workplace.isInvoiceWorkplace"></v-switch>
        </v-col>
      </v-row>
      <v-text-field
        class="mt-n1"
        outlined
        :label="$t('fields.invoice-address-attention')"
        v-model="workplace.invoiceAddressAttention"
      />
      <v-text-field
        class="mt-n7"
        outlined
        :label="$t('fields.invoice-address-email')"
        v-model="workplace.invoiceAddressEmail"
      />
      <p class="caption mt-n6">{{ $t('fields.delivery-address-text') }}</p>
      <h4 class="mt-9">{{ $t('fields.deliveries') }}</h4>
      <v-row no-gutters dense class="title-icon mt-n2">
        <v-col cols="10">
          <p>{{ $t('company.workplace-text2') }}</p>
        </v-col>
        <v-col class="text-right align-self-center" cols="2">
          <v-switch v-model="workplace.isDeliveryWorkplace"></v-switch>
        </v-col>
      </v-row>
      <v-text-field
        class="mt-n1"
        outlined
        :label="$t('fields.delivery-address-attention')"
        v-model="workplace.deliveryAddressAttention"
      />

      <v-text-field
        class="mt-n7"
        outlined
        :label="$t('fields.phone')"
        v-model="workplace.deliveryPhone"
        :rules="workplace.isDeliveryWorkplace ? [rules.required, rules.phone] : undefined"
      />
      
      <ActionButton class="mt-6" @click="saveWorkplaceData" actionIcon="$sl-save" :isDisabled="!isAdminRole || isImitatingUser()">{{
        $t('labels.save')
      }}</ActionButton>
      <ActionButton
        v-if="$route.params.workplace_id != 'new'"
        flat
        type="application"
        class="mt-6"
        actionIcon="$sl-delete"
        @click="showDelete = true"
        :isDisabled="!isAdminRole || $route.params.workplace_id == '0' || isImitatingUser()"
        >{{ $t('labels.btnActionDelete') }}</ActionButton
      >
    </v-form>
    <div v-else>
      <div v-if="workplace">
        <div v-if="workplace.name">
          <div class="overline">{{ $t('labels.workplace-name') }}</div>
          <p>{{ workplace.name }}</p>
        </div>

        <div v-if="workplace.addressee">
          <div class="overline">{{ $t('fields.Adressat') }}</div>
          <p>{{ workplace.addressee }}</p>
        </div>

        <div v-if="workplace.postalAddress1">
          <div class="overline">{{ $t('fields.address') }}</div>
          <p>{{ workplace.postalAddress1 }}</p>
        </div>

        <div v-if="workplace.postalAddress2">
          <div class="overline">{{ $t('fields.address2') }}</div>
          <p>{{ workplace.postalAddress2 }}</p>
        </div>

        <div v-if="workplace.postalZip">
          <div class="overline">{{ $t('fields.zip')}}</div>
          <p>{{ workplace.postalZip | formatZip }}</p>
        </div>

        <div v-if="workplace.postalCity">
          <div class="overline">{{ $t('fields.city') }}</div>
          <p>{{ workplace.postalCity }}</p>
        </div>

        <div v-if="workplace.postalCountry">
          <CountrySelector
            class="disabledTextfield"
            :showToggle="false"
            :label="$t('fields.country')"
            v-model="workplace.postalCountry"
          />
        </div>

        <div v-if="workplace.email">
          <div class="overline mt-n3">{{ $t('fields.email') }}</div>
          <p>{{ workplace.email }}</p>
        </div>

        <div v-if="workplace.web">
          <div class="overline">{{ $t('fields.web') }}</div>
          <p>{{ workplace.web }}</p>
        </div>

        <div v-if="workplace.phone">
          <div class="overline">{{ $t('fields.phone') }}</div>
          <p>{{ workplace.phone }}</p>
        </div>
        <template v-if="hasDepartments($route.params.id)">
          <div class="overline">{{ $t('company.sections') }}</div>
          <p>{{ getDepartmentNames(workplace.departmentIds) }}</p>
        </template>

        <div v-if="workplace.invoiceAddressAttention">
          <h4>{{ $t('fields.invoices') }}</h4>
          <div class="overline">{{ $t('fields.invoice-address-attention') }}</div>
          <p>{{ workplace.invoiceAddressAttention }}</p>
        </div>

        <div v-if="workplace.invoiceAddressEmail">
          <div class="overline">{{ $t('fields.invoice-address-email') }}</div>
          <p>{{ workplace.invoiceAddressEmail }}</p>
          <h4>{{ $t('fields.deliveries') }}</h4>
        </div>

        <div v-if="workplace.deliveryAddressAttention">
          <div class="overline">{{ $t('fields.delivery-address-attention') }}</div>
          <p>{{ workplace.deliveryAddressAttention }}</p>
        </div>
      </div>
    </div>
    <DeleteWorkplaceModal :show="showDelete" @close="showDelete = false" />
  </v-container>
</template>

<script>
import API from '@/services/api.js'
import {mapGetters} from 'vuex'
import findIndex from 'lodash/findIndex'
import MessageBox from '@/components/Common/MessageBox.vue'
import GoBackAtTop from '@/components/Common/GoBackAtTop'
import {RulesMixin, CompanyRoles} from '@/components/mixins'
import EnablableTextField from '@/components/Common/EnablableTextField'
import CountrySelector from '@/components/Common/CountrySelector'
import ActionButton from '@/components/Common/ActionButton'
import DeleteWorkplaceModal from '@/components/Company/DeleteWorkplaceModal'

export default {
  name: 'CompanyWorkplace',

  mixins: [RulesMixin, CompanyRoles],

  components: {
    GoBackAtTop,
    EnablableTextField,
    CountrySelector,
    ActionButton,
    MessageBox,
    DeleteWorkplaceModal
  },

  data: () => ({
    workplace: {},    
    departments: [],
    company: {},
    tab: 0,
    toAddress: '',
    address2Disabled: true,
    showDelete: false,
    valid: false
  }),

  created() {
    if (this.$refs.form) {
      this.$refs.form.resetValidation()
    }
    if (this.$route && this.$route.params && this.$route.params.tab) {
      this.tab = this.$route.params.tab
    }

    this.init()
  },
  computed: {
    ...mapGetters('currentuser', ['isAdminUser', 'isAdministrator', 'isDepartmentAdministrator', 'isImitatingUser']),

    isAdminRole() {
      return this.isAdministrator || this.isDepartmentAdministrator
    }
  },
  methods: {
    init() {      
      if (this.$route.params.workplace_id == 'new') {
        this.workplace = {postalCountry: 'SE'}
      } else {
        //- Workplace
        API.getWorkplace(this.$route.params.id, this.$route.params.workplace_id).then(result => {
          this.workplace = result.data
          //console.log('wp::', this.workplace)
        })
      }
      if (this.hasDepartments(this.$route.params.id) == true) {
        this.listDepartments()
      }
    },
    removeDepartment(item) {
      //workplace.departmentIds
      this.workplace.departmentIds.splice(this.workplace.departmentIds.indexOf(item.value), 1)
    },
    getDepartmentNames(ids) {
      let ret = []
      if (ids && ids.length > 0) {
        ids.forEach(id => {
          const found = this.departments.findIndex(x => x.value == id)
          if (found > -1) {
            ret.push(this.departments[found].text)
          }
        })
        return ret.join(', ')
      } else {
        return '-'
      }
    },
    listDepartments() {
      if (!(this.$route && this.$route.params && this.$route.params.id)) return

      API.listDepartments(this.$route.params.id)
        .then(response => {
          //console.log('listDep', response.data)
          this.departments = response.data.map(o => {
            return {
              text: o.name,
              value: o.id
            }
          })

          response.data.forEach(o => {})
        })
        .catch(error => {
          console.log('There was an error:', error)
        })
    },
    saveWorkplaceData() {
      if (!this.$refs.form.validate()) {
        this.$events.fire('showError', this.$t('errors.form-validation'))
        return
      }
      
      if (this.$route.params.workplace_id == 'new') {
        //-New
        API.createWorkplace(this.$route.params.id, {data: this.workplace})
          .then(result => {
            this.$events.fire('showInfo', this.$t('success.saved'))
            if (this.$refs.form) {
              this.$refs.form.reset()
            }
            this.$nextTick(() => {
              this.$router.push({name: 'CompanyWorkplaces', params: {id: this.$route.params.id}})
            })
          })
          .catch(error => {
            this.$events.fire('showError', this.$t('errors.save-failure'))
            console.log('There was an error:', error)
          })
      } else {              
        API.updateWorkplace(this.$route.params.id, this.$route.params.workplace_id, {data: this.workplace})
          .then(result => {
            this.$events.fire('showInfo', this.$t('success.saved'))            
            this.$nextTick(() => {
              this.$router.push({name: 'CompanyWorkplaces', params: {id: this.$route.params.id}})
            })
          })
          .catch(error => {
            this.$events.fire('showError', this.$t('errors.save-failure'))
            console.log('There was an error:', error)
          })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
</style>